import { MoreVertical } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

type MenuAction = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
};

type ThreeDotsMenuProps = {
  actions: MenuAction[];
  buttonClassName?: string;
};

const ThreeDotsMenu: React.FC<ThreeDotsMenuProps> = ({
  actions,
  buttonClassName,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: "bottom-right",
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate optimal position for dropdown
  const calculatePosition = () => {
    if (!buttonRef.current || !dropdownRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let top = buttonRect.bottom + 8; // Default: below button
    let left = buttonRect.right - 192; // Default: align right (192px = w-48)
    let placement = "bottom-right";

    // Check if dropdown fits below
    if (top + dropdownRect.height > viewport.height) {
      // Try above
      if (buttonRect.top - dropdownRect.height - 8 >= 0) {
        top = buttonRect.top - dropdownRect.height - 8;
        placement = placement.replace("bottom", "top");
      } else {
        // If neither above nor below works, center vertically
        top = Math.max(
          8,
          Math.min(
            viewport.height - dropdownRect.height - 8,
            buttonRect.top + buttonRect.height / 2 - dropdownRect.height / 2
          )
        );
        placement = placement.replace("bottom", "middle");
      }
    }

    // Check if dropdown fits on the right side
    if (left + 192 > viewport.width) {
      // Try left side
      if (buttonRect.left - 192 >= 0) {
        left = buttonRect.left - 192;
        placement = placement.replace("right", "left");
      } else {
        // Center horizontally if neither side works
        left = Math.max(
          8,
          Math.min(
            viewport.width - 192 - 8,
            buttonRect.left + buttonRect.width / 2 - 96
          )
        );
        placement = placement.replace("right", "center");
      }
    }

    setPosition({ top, left, placement });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideButton =
        menuRef.current && menuRef.current.contains(target);
      const isInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(target);

      if (!isInsideButton && !isInsideDropdown) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  // Calculate position when dropdown opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure dropdown is rendered
      const timeoutId = setTimeout(calculatePosition, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  // Recalculate position on window resize and scroll
  useEffect(() => {
    if (open) {
      const handleResize = () => calculatePosition();
      const handleScroll = () => calculatePosition();

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true); // true for capture phase to catch all scroll events
      document.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
        document.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [open]);

  return (
    <>
      <div className="relative inline-block text-left" ref={menuRef}>
        <button
          ref={buttonRef}
          onClick={() => setOpen((prev) => !prev)}
          className={`p-2 rounded-full hover:bg-gray-100 focus:outline-none transition-colors ${buttonClassName}`}
        >
          <MoreVertical className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Portal-like dropdown positioned absolutely on viewport */}
      {open && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-48 bg-white border border-gray-200 rounded-md shadow-lg animate-in fade-in-0 zoom-in-95 duration-100"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                setOpen(false);
                action.onClick();
              }}
              className="w-full flex items-center px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md transition-colors"
            >
              {action.icon && <span className="mr-2">{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default ThreeDotsMenu;
